import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Layout } from '../../components/Layout';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../../components/ui/dialog';
import { getInitials } from '../../lib/utils';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Textarea } from '../../components/ui/textarea';
import { Avatar, AvatarFallback, AvatarImage } from '../../components/ui/avatar';
import { Switch } from '../../components/ui/switch';
import { toast } from 'sonner';
import { format, parseISO, startOfMonth, endOfMonth, eachDayOfInterval, isSameMonth, isToday, isSameDay, addMonths, subMonths } from 'date-fns';
import { 
  Palmtree,
  Search,
  Check,
  X,
  Clock,
  ChevronLeft,
  ChevronRight,
  Calendar,
  Users,
  AlertCircle,
  CheckCircle2,
  XCircle,
  Plus,
  Trash2,
  Edit,
  CalendarDays,
  Umbrella,
  Heart,
  User,
  PartyPopper,
  GraduationCap,
  Baby,
  Briefcase,
  Filter
} from 'lucide-react';
import { useTranslation } from 'react-i18next';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Leave type icons mapping
const leaveTypeIcons = {
  annual: Umbrella,
  sick: Heart,
  personal: User,
  maternity: Baby,
  study_leave: GraduationCap,
  default: Palmtree
};

const getLeaveIcon = (type) => {
  return leaveTypeIcons[type] || leaveTypeIcons.default;
};

// Format name as "FirstName L." (first name + last name initial)
const formatShortName = (fullName) => {
  if (!fullName) return '';
  const parts = fullName.trim().split(' ');
  if (parts.length === 1) return parts[0];
  return `${parts[0]} ${parts[parts.length - 1][0]}.`;
};

const getLeaveColor = (type) => {
  const colors = {
    annual: 'bg-blue-100 text-blue-700 dark:bg-blue-900/20 dark:text-blue-300',
    sick: 'bg-red-100 text-red-700 dark:bg-red-900/20 dark:text-red-300',
    personal: 'bg-purple-100 text-purple-700 dark:bg-purple-900/20 dark:text-purple-300',
    maternity: 'bg-pink-100 text-pink-700 dark:bg-pink-900/20 dark:text-pink-300',
    study_leave: 'bg-amber-100 text-amber-700 dark:bg-amber-900/20 dark:text-amber-300',
    default: 'bg-slate-100 dark:bg-slate-700 text-slate-700 dark:bg-slate-50 dark:bg-slate-7000/20 dark:text-slate-300'
  };
  return colors[type] || colors.default;
};

export default function TimeOffManagement() {
  const { user, token } = useAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();
  
  // State
  const [requests, setRequests] = useState([]);
  const [stats, setStats] = useState({
    pending_count: 0,
    approved_this_month: 0,
    off_today_count: 0,
    off_today: [],
    total_employees: 0
  });
  const [balancesData, setBalancesData] = useState({ employees: [], leave_types: [] });
  const [leaveTypes, setLeaveTypes] = useState([]);
  const [holidays, setHolidays] = useState([]);
  const [calendarData, setCalendarData] = useState({ events: [], holidays: [], employees: [] });
  const [loading, setLoading] = useState(true);
  
  // Main Tab
  const [mainTab, setMainTab] = useState('requests');
  
  // Request Filters
  const [requestTab, setRequestTab] = useState('pending');
  const [searchQuery, setSearchQuery] = useState('');
  const [leaveTypeFilter, setLeaveTypeFilter] = useState('all');
  
  // Calendar State
  const [calendarMonth, setCalendarMonth] = useState(new Date());
  
  // Modals
  const [approveModalOpen, setApproveModalOpen] = useState(false);
  const [rejectModalOpen, setRejectModalOpen] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [rejectReason, setRejectReason] = useState('');
  
  // Balance Edit Modal
  const [balanceModalOpen, setBalanceModalOpen] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [editingBalances, setEditingBalances] = useState({});
  
  // Bulk Balance Modal
  const [bulkBalanceModalOpen, setBulkBalanceModalOpen] = useState(false);
  const [bulkBalanceType, setBulkBalanceType] = useState('');
  const [bulkBalanceValue, setBulkBalanceValue] = useState(0);

  // Access check
  useEffect(() => {
    if (user && !['admin', 'manager'].includes(user.role)) {
      navigate('/dashboard');
      toast.error('You do not have access to this page');
    }
  }, [user, navigate]);

  // Fetch functions
  const fetchRequests = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (requestTab !== 'all') params.append('status', requestTab);
      if (searchQuery) params.append('search', searchQuery);
      if (leaveTypeFilter !== 'all') params.append('leave_type', leaveTypeFilter);

      const res = await fetch(`${API_URL}/api/admin/time-off?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      setRequests(data);
    } catch (error) {
      console.error('Failed to fetch requests:', error);
    }
  }, [token, requestTab, searchQuery, leaveTypeFilter]);

  const fetchStats = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/admin/time-off/stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      setStats(data);
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    }
  }, [token]);

  const fetchBalances = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/admin/time-off/balances`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      setBalancesData(data);
    } catch (error) {
      console.error('Failed to fetch balances:', error);
    }
  }, [token]);

  const fetchLeaveTypes = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/admin/time-off/leave-types`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      setLeaveTypes(data);
    } catch (error) {
      console.error('Failed to fetch leave types:', error);
    }
  }, [token]);

  const fetchHolidays = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/admin/time-off/holidays`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      setHolidays(data);
    } catch (error) {
      console.error('Failed to fetch holidays:', error);
    }
  }, [token]);

  const fetchCalendar = useCallback(async () => {
    try {
      const month = calendarMonth.getMonth() + 1;
      const year = calendarMonth.getFullYear();
      const res = await fetch(`${API_URL}/api/admin/time-off/calendar?month=${month}&year=${year}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      setCalendarData(data);
    } catch (error) {
      console.error('Failed to fetch calendar:', error);
    }
  }, [token, calendarMonth]);

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([fetchRequests(), fetchStats(), fetchBalances(), fetchLeaveTypes(), fetchHolidays(), fetchCalendar()]);
      setLoading(false);
    };
    if (token) loadData();
  }, [token, fetchRequests, fetchStats, fetchBalances, fetchLeaveTypes, fetchHolidays, fetchCalendar]);

  // Handlers
  const handleApprove = async () => {
    if (!selectedRequest) return;

    try {
      const res = await fetch(`${API_URL}/api/admin/time-off/${selectedRequest.id}/approve`, {
        method: 'PUT',
        headers: { Authorization: `Bearer ${token}` }
      });

      if (!res.ok) {
        const error = await res.json();
        throw new Error(error.detail || 'Failed to approve');
      }

      toast.success('Time off request approved');
      setApproveModalOpen(false);
      setSelectedRequest(null);
      fetchRequests();
      fetchStats();
      fetchBalances();
      fetchCalendar();
    } catch (error) {
      toast.error(error.message);
    }
  };

  const handleReject = async () => {
    if (!selectedRequest || rejectReason.length < 10) {
      toast.error('Rejection reason must be at least 10 characters');
      return;
    }

    try {
      const res = await fetch(`${API_URL}/api/admin/time-off/${selectedRequest.id}/reject`, {
        method: 'PUT',
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ reason: rejectReason })
      });

      if (!res.ok) {
        const error = await res.json();
        throw new Error(error.detail || 'Failed to reject');
      }

      toast.success('Time off request rejected');
      setRejectModalOpen(false);
      setRejectReason('');
      setSelectedRequest(null);
      fetchRequests();
      fetchStats();
    } catch (error) {
      toast.error(error.message);
    }
  };

  const handleUpdateBalance = async () => {
    if (!selectedEmployee) return;

    try {
      for (const [leaveType, total] of Object.entries(editingBalances)) {
        await fetch(`${API_URL}/api/admin/time-off/balances/${selectedEmployee.id}`, {
          method: 'PUT',
          headers: { 
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ leave_type: leaveType, total_days: total })
        });
      }

      toast.success('Balances updated');
      setBalanceModalOpen(false);
      setSelectedEmployee(null);
      setEditingBalances({});
      fetchBalances();
    } catch (error) {
      toast.error('Failed to update balances');
    }
  };

  const handleBulkUpdateBalance = async () => {
    if (!bulkBalanceType || bulkBalanceValue < 0) {
      toast.error('Please select a leave type and enter a valid value');
      return;
    }

    try {
      const res = await fetch(`${API_URL}/api/admin/time-off/balances/bulk`, {
        method: 'PUT',
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ leave_type: bulkBalanceType, total_days: bulkBalanceValue })
      });

      if (!res.ok) {
        const error = await res.json();
        throw new Error(error.detail || 'Failed to bulk update');
      }

      toast.success('All employee balances updated');
      setBulkBalanceModalOpen(false);
      setBulkBalanceType('');
      setBulkBalanceValue(0);
      fetchBalances();
    } catch (error) {
      toast.error(error.message);
    }
  };

  // Calendar helpers
  const calendarDays = eachDayOfInterval({
    start: startOfMonth(calendarMonth),
    end: endOfMonth(calendarMonth)
  });

  const getEventsForDay = (day) => {
    const dateStr = format(day, 'yyyy-MM-dd');
    return calendarData.events.filter(event => {
      return dateStr >= event.start_date && dateStr <= event.end_date;
    });
  };

  const getHolidayForDay = (day) => {
    const dateStr = format(day, 'yyyy-MM-dd');
    return calendarData.holidays.find(h => h.date === dateStr);
  };

  // Helpers
  const getStatusBadge = (status) => {
    switch (status) {
      case 'pending': return 'bg-amber-100 text-amber-700 dark:bg-amber-900/20 dark:text-amber-300';
      case 'approved': return 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/20 dark:text-emerald-300';
      case 'rejected': return 'bg-red-100 text-red-700 dark:bg-red-900/20 dark:text-red-300';
      case 'cancelled': return 'bg-slate-100 dark:bg-slate-700 text-slate-700 dark:bg-slate-50 dark:bg-slate-7000/20 dark:text-slate-300';
      default: return 'bg-slate-100 dark:bg-slate-700 text-slate-700';
    }
  };

  const formatDateRange = (start, end) => {
    try {
      const startDate = parseISO(start);
      const endDate = parseISO(end);
      if (start === end) {
        return format(startDate, 'MMM d, yyyy');
      }
      return `${format(startDate, 'MMM d')} - ${format(endDate, 'MMM d, yyyy')}`;
    } catch {
      return `${start} - ${end}`;
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6" data-testid="time-off-management-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <div className="flex items-center gap-3">
              <h1 className="text-2xl font-bold text-slate-900 dark:text-white">{t('nav.timeOff')}</h1>
              {stats.pending_count > 0 && (
                <Badge className="bg-amber-500 dark:bg-amber-600 text-white">{stats.pending_count} pending</Badge>
              )}
            </div>
            <p className="text-slate-500 dark:text-slate-400 mt-1">{t('timeOff.subtitle')}</p>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 gap-4">
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-amber-100 dark:bg-amber-900/20 rounded-lg">
                  <Clock className="w-5 h-5 text-amber-600 dark:text-amber-400" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-900 dark:text-white" data-testid="pending-count">{stats.pending_count}</p>
                  <p className="text-sm text-slate-500">Pending Requests</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-purple-200 dark:border-purple-500/30 bg-purple-50 dark:bg-purple-900/30/50 dark:bg-purple-900/5">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-purple-100 dark:bg-purple-900/20 rounded-lg">
                  <Palmtree className="w-5 h-5 text-purple-600 dark:text-purple-400" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-900 dark:text-white" data-testid="off-today-count">{stats.off_today_count}</p>
                  <p className="text-sm text-slate-500">Off Today</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Who's Off Today Section */}
        {stats.off_today && stats.off_today.length > 0 && (
          <Card className="border-purple-200 dark:border-purple-500/30">
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2">
                <Palmtree className="w-4 h-4 text-purple-600" />
                Who's Off Today
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex flex-wrap gap-3">
                {stats.off_today.map((person) => (
                  <div key={person.id} className="flex items-center gap-2 px-3 py-2 bg-purple-50 dark:bg-purple-900/10 rounded-lg">
                    <Avatar className="h-8 w-8">
                      {person.profile_photo && <AvatarImage src={person.profile_photo} alt={person.name} />}
                      <AvatarFallback className="bg-purple-600 text-white text-xs">
                        {getInitials(person.name)}
                      </AvatarFallback>
                    </Avatar>
                    <div>
                      <p className="text-sm font-medium text-slate-900 dark:text-white">{person.name}</p>
                      <p className="text-xs text-purple-600 dark:text-purple-400 capitalize">{person.leave_type?.replace('_', ' ')}</p>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Main Tabs */}
        <Tabs value={mainTab} onValueChange={setMainTab} className="space-y-4">
          <TabsList>
            <TabsTrigger value="requests" data-testid="tab-requests">
              <Clock className="w-4 h-4 mr-2" />
              Requests
            </TabsTrigger>
            {user?.role === 'admin' && (
              <TabsTrigger value="balances" data-testid="tab-balances">
                <Users className="w-4 h-4 mr-2" />
                Balances
              </TabsTrigger>
            )}
          </TabsList>

          {/* REQUESTS TAB */}
          <TabsContent value="requests" className="space-y-4">
            {/* Request Filters */}
            <Card>
              <CardContent className="p-4">
                <div className="flex flex-col md:flex-row gap-4">
                  <div className="relative flex-1">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input
                      placeholder="Search by employee name..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="pl-10"
                      data-testid="search-input"
                    />
                  </div>
                  <Select value={leaveTypeFilter} onValueChange={setLeaveTypeFilter}>
                    <SelectTrigger className="w-[180px]" data-testid="leave-type-filter">
                      <SelectValue placeholder="Leave Type" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Types</SelectItem>
                      {leaveTypes.map(type => (
                        <SelectItem key={type.key} value={type.key}>{type.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </CardContent>
            </Card>

            {/* Request Status Tabs */}
            <Tabs value={requestTab} onValueChange={setRequestTab}>
              <TabsList>
                <TabsTrigger value="pending" data-testid="status-pending">
                  Pending
                  {stats.pending_count > 0 && (
                    <Badge className="ml-2 bg-amber-500 dark:bg-amber-600 text-white text-xs">{stats.pending_count}</Badge>
                  )}
                </TabsTrigger>
                <TabsTrigger value="approved" data-testid="status-approved">Approved</TabsTrigger>
                <TabsTrigger value="rejected" data-testid="status-rejected">Rejected</TabsTrigger>
                <TabsTrigger value="all" data-testid="status-all">All</TabsTrigger>
              </TabsList>
            </Tabs>

            {/* Requests Table */}
            <Card>
              <CardContent className="p-0">
                <div className="overflow-x-auto">
                  <table className="w-full" data-testid="requests-table">
                    <thead className="bg-slate-50 dark:bg-slate-800/50 border-b border-slate-200 dark:border-slate-700">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">Employee</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">Leave Type</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">Dates</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">Days</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">Balance Left</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">Status</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">Actioned By</th>
                        <th className="px-4 py-3 text-right text-xs font-semibold text-slate-500 uppercase tracking-wider">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-200 dark:divide-slate-700">
                      {requests.map((request) => {
                        const LeaveIcon = getLeaveIcon(request.leave_type);
                        const leaveTypeName = request.is_archived_leave_type 
                          ? `[Archived] ${request.leave_type?.replace('_', ' ')}`
                          : request.leave_type?.replace('_', ' ');
                        return (
                          <tr key={request.id} className="hover:bg-slate-50 dark:hover:bg-slate-800/50" data-testid={`request-row-${request.id}`}>
                            <td className="px-4 py-3">
                              <div className="flex items-center gap-3">
                                <Avatar className="h-9 w-9">
                                  {request.employee?.profile_photo && <AvatarImage src={request.employee.profile_photo} alt={request.employee?.name} />}
                                  <AvatarFallback className="bg-gradient-to-br from-blue-500 to-purple-600 text-white text-sm">
                                    {getInitials(request.employee?.name)}
                                  </AvatarFallback>
                                </Avatar>
                                <div>
                                  <p className="font-medium text-slate-900 dark:text-white">{request.employee?.name}</p>
                                  <p className="text-xs text-slate-500">{request.employee?.department || <span className="italic">Not assigned</span>}</p>
                                </div>
                              </div>
                            </td>
                            <td className="px-4 py-3">
                              <Badge className={request.is_archived_leave_type ? 'bg-slate-100 dark:bg-slate-700 text-slate-500' : getLeaveColor(request.leave_type)}>
                                <LeaveIcon className="w-3 h-3 mr-1" />
                                {leaveTypeName}
                              </Badge>
                            </td>
                            <td className="px-4 py-3 text-slate-600 dark:text-slate-300">
                              {formatDateRange(request.start_date, request.end_date)}
                            </td>
                            <td className="px-4 py-3 font-medium text-slate-900 dark:text-white">
                              {request.working_days} day{request.working_days !== 1 ? 's' : ''}
                            </td>
                            <td className="px-4 py-3">
                              {request.is_archived_leave_type ? (
                                <span className="text-slate-400 italic text-xs">N/A</span>
                              ) : request.remaining_balance !== null ? (
                                <span className={request.remaining_balance < 0 ? 'text-red-600' : 'text-slate-600 dark:text-slate-300'}>
                                  {request.remaining_balance} days
                                </span>
                              ) : '-'}
                            </td>
                            <td className="px-4 py-3">
                              <Badge className={getStatusBadge(request.status)}>
                                {request.status?.charAt(0).toUpperCase() + request.status?.slice(1)}
                              </Badge>
                            </td>
                            <td className="px-4 py-3 text-slate-600 dark:text-slate-300">
                              {request.status === 'approved' ? request.approver_name || '-' : 
                               request.status === 'rejected' ? request.rejector_name || '-' : '-'}
                            </td>
                            <td className="px-4 py-3 text-right">
                              {request.status === 'pending' && (
                                <div className="flex items-center justify-end gap-2">
                                  <Button
                                    size="sm"
                                    className="bg-emerald-600 hover:bg-emerald-700"
                                    onClick={() => {
                                      setSelectedRequest(request);
                                      setApproveModalOpen(true);
                                    }}
                                    data-testid={`approve-btn-${request.id}`}
                                  >
                                    <Check className="w-4 h-4 mr-1" />
                                    Approve
                                  </Button>
                                  <Button
                                    size="sm"
                                    variant="destructive"
                                    onClick={() => {
                                      setSelectedRequest(request);
                                      setRejectModalOpen(true);
                                    }}
                                    data-testid={`reject-btn-${request.id}`}
                                  >
                                    <X className="w-4 h-4 mr-1" />
                                    Reject
                                  </Button>
                                </div>
                              )}
                              {request.status === 'rejected' && request.rejection_note && (
                                <span className="text-sm text-red-600" title={request.rejection_note}>
                                  Reason: {request.rejection_note.substring(0, 25)}...
                                </span>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                      {requests.length === 0 && (
                        <tr>
                          <td colSpan={7} className="px-4 py-12 text-center text-slate-500">
                            No time off requests found
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          </TabsContent>



          {/* BALANCES TAB */}
          <TabsContent value="balances" className="space-y-4">
            <div className="flex justify-end">
              {user?.role === 'admin' && (
                <Button onClick={() => setBulkBalanceModalOpen(true)} data-testid="bulk-balance-btn">
                  <Edit className="w-4 h-4 mr-2" />
                  Bulk Edit Balances
                </Button>
              )}
            </div>
            
            <Card>
              <CardContent className="p-0">
                <div className="overflow-x-auto">
                  <table className="w-full" data-testid="balances-table">
                    <thead className="bg-slate-50 dark:bg-slate-800/50 border-b border-slate-200 dark:border-slate-700">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">Employee</th>
                        {balancesData.leave_types.map(type => (
                          <th key={type.key} className="px-4 py-3 text-center text-xs font-semibold text-slate-500 uppercase tracking-wider">
                            {type.name}
                          </th>
                        ))}
                        {user?.role === 'admin' && (
                          <th className="px-4 py-3 text-right text-xs font-semibold text-slate-500 uppercase tracking-wider">Actions</th>
                        )}
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-200 dark:divide-slate-700">
                      {balancesData.employees.map((emp) => (
                        <tr key={emp.id} className="hover:bg-slate-50 dark:hover:bg-slate-800/50" data-testid={`balance-row-${emp.id}`}>
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-3">
                              <Avatar className="h-9 w-9">
                                {emp.profile_photo && <AvatarImage src={emp.profile_photo} alt={emp.name} />}
                                <AvatarFallback className="bg-gradient-to-br from-blue-500 to-purple-600 text-white text-sm">
                                  {getInitials(emp.name)}
                                </AvatarFallback>
                              </Avatar>
                              <div>
                                <p className="font-medium text-slate-900 dark:text-white">{emp.name}</p>
                                <p className="text-xs text-slate-500">{emp.department || <span className="italic">Not assigned</span>}</p>
                              </div>
                            </div>
                          </td>
                          {balancesData.leave_types.map(type => {
                            const balance = emp.balances[type.key] || { total: 0, used: 0 };
                            const remaining = balance.total - balance.used;
                            // Color logic: red if negative, amber if low (< 20% remaining), gray if 0/0, else normal
                            const isZeroOfZero = balance.total === 0 && balance.used === 0;
                            const remainingPercent = balance.total > 0 ? (remaining / balance.total) * 100 : 0;
                            const colorClass = remaining < 0 
                              ? 'text-red-600' 
                              : isZeroOfZero 
                                ? 'text-slate-400' 
                                : remainingPercent < 20 
                                  ? 'text-amber-600' 
                                  : 'text-slate-900 dark:text-white';
                            return (
                              <td key={type.key} className="px-4 py-3 text-center">
                                <div className="flex flex-col items-center">
                                  <span className={`font-medium ${colorClass}`}>
                                    {remaining}
                                  </span>
                                  <span className="text-xs text-slate-400">of {balance.total}</span>
                                </div>
                              </td>
                            );
                          })}
                          {user?.role === 'admin' && (
                            <td className="px-4 py-3 text-right">
                              <Button 
                                variant="ghost" 
                                size="sm"
                                onClick={() => {
                                  setSelectedEmployee(emp);
                                  setEditingBalances(
                                    balancesData.leave_types.reduce((acc, type) => {
                                      acc[type.key] = emp.balances[type.key]?.total || 0;
                                      return acc;
                                    }, {})
                                  );
                                  setBalanceModalOpen(true);
                                }}
                                data-testid={`edit-balance-btn-${emp.id}`}
                              >
                                <Edit className="w-4 h-4" />
                              </Button>
                            </td>
                          )}
                        </tr>
                      ))}
                      {balancesData.employees.length === 0 && (
                        <tr>
                          <td colSpan={balancesData.leave_types.length + 2} className="px-4 py-12 text-center text-slate-500">
                            No employees found
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* MODALS */}
        
        {/* Approve Modal */}
        <Dialog open={approveModalOpen} onOpenChange={setApproveModalOpen}>
          <DialogContent data-testid="approve-modal">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-emerald-600">
                <CheckCircle2 className="w-5 h-5" />
                Approve Time Off Request
              </DialogTitle>
              <DialogDescription>
                Approve request for <strong>{selectedRequest?.employee?.name}</strong>
              </DialogDescription>
            </DialogHeader>
            <div className="py-4">
              <div className="p-4 bg-emerald-50 dark:bg-emerald-900/10 rounded-lg space-y-2">
                <div className="flex justify-between">
                  <span className="text-slate-500">Leave Type:</span>
                  <span className="font-medium capitalize">{selectedRequest?.leave_type?.replace('_', ' ')}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-500">Dates:</span>
                  <span className="font-medium">{selectedRequest && formatDateRange(selectedRequest.start_date, selectedRequest.end_date)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-500">Duration:</span>
                  <span className="font-medium">{selectedRequest?.working_days} working day(s)</span>
                </div>
                {selectedRequest?.reason && (
                  <div className="pt-2 border-t">
                    <span className="text-sm text-slate-500">Reason: {selectedRequest.reason}</span>
                  </div>
                )}
              </div>
              <p className="mt-3 text-sm text-slate-500">
                This will automatically deduct {selectedRequest?.working_days} day(s) from their {selectedRequest?.leave_type?.replace('_', ' ')} balance.
              </p>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setApproveModalOpen(false)}>Cancel</Button>
              <Button onClick={handleApprove} className="bg-emerald-600 hover:bg-emerald-700" data-testid="confirm-approve-btn">
                <Check className="w-4 h-4 mr-2" />
                Confirm Approve
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Reject Modal */}
        <Dialog open={rejectModalOpen} onOpenChange={setRejectModalOpen}>
          <DialogContent data-testid="reject-modal">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-red-600">
                <XCircle className="w-5 h-5" />
                Reject Time Off Request
              </DialogTitle>
              <DialogDescription>
                Reject request for <strong>{selectedRequest?.employee?.name}</strong>. Please provide a reason.
              </DialogDescription>
            </DialogHeader>
            <div className="py-4 space-y-4">
              <div className="p-3 bg-slate-50 dark:bg-slate-800 rounded-lg text-sm">
                <p><strong>Type:</strong> {selectedRequest?.leave_type?.replace('_', ' ')}</p>
                <p><strong>Dates:</strong> {selectedRequest && formatDateRange(selectedRequest.start_date, selectedRequest.end_date)}</p>
              </div>
              <div className="space-y-2">
                <Label>Rejection Reason *</Label>
                <Textarea
                  value={rejectReason}
                  onChange={(e) => setRejectReason(e.target.value)}
                  placeholder="Please explain why this request is being rejected (min 10 characters)..."
                  rows={4}
                  data-testid="reject-reason-input"
                />
                <p className="text-xs text-slate-500">{rejectReason.length}/10 characters minimum</p>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => { setRejectModalOpen(false); setRejectReason(''); }}>Cancel</Button>
              <Button 
                variant="destructive" 
                onClick={handleReject}
                disabled={rejectReason.length < 10}
                data-testid="confirm-reject-btn"
              >
                <X className="w-4 h-4 mr-2" />
                Reject Request
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Edit Balance Modal */}
        <Dialog open={balanceModalOpen} onOpenChange={setBalanceModalOpen}>
          <DialogContent data-testid="balance-modal">
            <DialogHeader>
              <DialogTitle>Edit Leave Balances</DialogTitle>
              <DialogDescription>Update balances for {selectedEmployee?.name}</DialogDescription>
            </DialogHeader>
            <div className="py-4 space-y-4">
              {balancesData.leave_types.map(type => (
                <div key={type.key} className="flex items-center justify-between">
                  <Label className="capitalize">{type.name}</Label>
                  <Input
                    type="number"
                    min="0"
                    className="w-24"
                    value={editingBalances[type.key] || 0}
                    onChange={(e) => setEditingBalances({ ...editingBalances, [type.key]: parseInt(e.target.value) || 0 })}
                    data-testid={`balance-input-${type.key}`}
                  />
                </div>
              ))}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setBalanceModalOpen(false)}>Cancel</Button>
              <Button onClick={handleUpdateBalance} data-testid="save-balance-btn">
                Save Changes
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Bulk Balance Modal */}
        <Dialog open={bulkBalanceModalOpen} onOpenChange={setBulkBalanceModalOpen}>
          <DialogContent data-testid="bulk-balance-modal">
            <DialogHeader>
              <DialogTitle>Bulk Edit Balances</DialogTitle>
              <DialogDescription>Set the same balance for all employees</DialogDescription>
            </DialogHeader>
            <div className="py-4 space-y-4">
              <div className="space-y-2">
                <Label>Leave Type</Label>
                <Select value={bulkBalanceType} onValueChange={setBulkBalanceType}>
                  <SelectTrigger data-testid="bulk-leave-type-select">
                    <SelectValue placeholder="Select leave type" />
                  </SelectTrigger>
                  <SelectContent>
                    {balancesData.leave_types.map(type => (
                      <SelectItem key={type.key} value={type.key}>{type.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Total Days</Label>
                <Input
                  type="number"
                  min="0"
                  value={bulkBalanceValue}
                  onChange={(e) => setBulkBalanceValue(parseInt(e.target.value) || 0)}
                  data-testid="bulk-balance-input"
                />
              </div>
              <p className="text-sm text-amber-600 flex items-center gap-2">
                <AlertCircle className="w-4 h-4" />
                This will update all {balancesData.employees.length} employees
              </p>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setBulkBalanceModalOpen(false)}>Cancel</Button>
              <Button onClick={handleBulkUpdateBalance} data-testid="confirm-bulk-balance-btn">
                Update All
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

      </div>
    </Layout>
  );
}
